import { useCallback, useMemo, useState } from 'react';
import type {
  DemandInsight,
  HubWeight,
  OperatingPeriodUtc,
  ServiceMode,
  ServicePreset,
  WizardState,
  WizardStepDescriptor,
  WizardStepId,
  WizardStepValidation,
  WizardTimelineSlotStatus,
} from './types';
import { FEEL_PROFILES } from './feelProfiles';

const WIZARD_DRAFT_STORAGE_KEY = 'atlas_onboardingWizard_draft_v1';
const WIZARD_DRAFT_VERSION = 1;
const MAX_WIZARD_GENERATED_TRIPS = 250;

const demandPatternByPreset: Record<ServicePreset, WizardState['demandPattern']> = {
  'on-demand': 'Commuter',
  school: 'School',
  paratransit: 'Midday',
};

const makeOperatingPeriod = (id: string, startUtc: string, endUtc: string): OperatingPeriodUtc => ({
  id,
  startUtc,
  endUtc,
});

const getDefaultPeriodsForPreset = (preset: ServicePreset | null): OperatingPeriodUtc[] => {
  if (preset === 'school') {
    return [makeOperatingPeriod('morning', '07:00', '09:00'), makeOperatingPeriod('afternoon', '14:00', '16:00')];
  }
  return [makeOperatingPeriod('default', '06:00', '22:00')];
};

const buildSimulationName = (preset: ServicePreset | null) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const presetLabel =
    preset === 'on-demand' ? 'On-Demand' : preset === 'school' ? 'School' : preset === 'paratransit' ? 'Paratransit' : 'Service';
  return `${presetLabel} Scenario ${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

const DEFAULT_PASSENGERS_NON_PARATRANSIT = { standardPct: 98, wheelchairPct: 2 };
const DEFAULT_PASSENGERS_PARATRANSIT = { standardPct: 90, wheelchairPct: 10 };

const STEP_LABELS: Record<WizardStepId, string> = {
  setup: 'Setup',
  configuration: 'Configuration',
  demand: 'Demand',
  passenger_types: 'Passenger Types',
  hub_weighting: 'Hub Weighting',
  fleet: 'Fleet',
  service_configuration: 'Service Configuration',
  summary: 'Summary',
};

function clampIntegerPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
}

function buildEqualIntegerSplit(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;
  return Array.from({ length: count }, (_, index) => (index < remainder ? base + 1 : base));
}

function distributeUnitsEvenly(total: number, count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(total / count);
  const remainder = total - base * count;
  return Array.from({ length: count }, (_, index) => (index < remainder ? base + 1 : base));
}

function rebalanceToExact100(weights: HubWeight[]): HubWeight[] {
  const next = weights.map((hub) => ({ ...hub, percentage: clampIntegerPercent(hub.percentage) }));
  let total = next.reduce((sum, hub) => sum + hub.percentage, 0);
  let guard = 0;

  while (total !== 100 && next.length > 0 && guard < 400) {
    guard += 1;
    const diff = 100 - total;
    if (diff > 0) {
      const idx = next.findIndex((hub) => hub.percentage < 100);
      if (idx < 0) break;
      next[idx].percentage += 1;
      total += 1;
      continue;
    }

    const idx = next.findIndex((hub) => hub.percentage > 0);
    if (idx < 0) break;
    next[idx].percentage -= 1;
    total -= 1;
  }

  return next;
}

function hubWeightsEquivalent(a: HubWeight[], b: HubWeight[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].id !== b[i].id) return false;
    if (a[i].name !== b[i].name) return false;
    if (a[i].percentage !== b[i].percentage) return false;
  }
  return true;
}

function computeWizardStepIds(state: WizardState): WizardStepId[] {
  const ids: WizardStepId[] = ['setup', 'configuration'];
  const requiresHubs =
    state.servicePreset !== 'paratransit' && (state.serviceMode === 'door_to_hub' || state.serviceMode === 'hub_to_hub');
  const requiresHubWeighting = requiresHubs && state.servicePreset !== 'school';
  if (requiresHubWeighting) {
    ids.push('hub_weighting');
  }
  ids.push('demand');
  ids.push('fleet', 'service_configuration', 'summary');
  return ids;
}

const initialState: WizardState = {
  currentStep: 1,
  servicePreset: 'on-demand',
  demandPattern: demandPatternByPreset['on-demand'],
  isMinimizedForMap: false,
  operatingPeriodsUtc: getDefaultPeriodsForPreset(null),
  serviceMode: null,
  hubCount: 0,
  hubBaselinePointCount: null,
  demandTrips: 100,
  demandHelpStatus: 'idle',
  generateTripsStatus: 'idle',
  demandInsight: null,
  demandError: null,
  demandHelperStatusText: null,
  rideSource: 'none',
  authoritativeRideLayerId: null,
  passengerDistribution: { ...DEFAULT_PASSENGERS_NON_PARATRANSIT },
  savedParatransitPassengerDistribution: { ...DEFAULT_PASSENGERS_PARATRANSIT },
  hubWeights: [],
  hubWeightsTouched: false,
  vehicleCount: 10,
  capacityStandard: 12,
  capacityWheelchair: 1,
  fleetHelpStatus: 'idle',
  fleetHelpOpen: false,
  fleetSource: 'none',
  authoritativeFleetId: null,
  fleetTimelineSlotStatuses: {},
  fleetDefaultsSeededFromDemand: false,
  feelLevel: 3,
  feelSlope: FEEL_PROFILES[3].slope,
  feelInterceptMinutes: FEEL_PROFILES[3].interceptMinutes,
  maxTripTimeMinutes: 75,
  simulationName: buildSimulationName('on-demand'),
};

function getHydratedInitialState(): WizardState {
  try {
    const navigationEntries = typeof performance !== 'undefined' ? performance.getEntriesByType('navigation') : [];
    const navigationType = (navigationEntries[0] as PerformanceNavigationTiming | undefined)?.type;
    const isBrowserRefresh = navigationType === 'reload';

    if (isBrowserRefresh) {
      localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
      return initialState;
    }

    const raw = localStorage.getItem(WIZARD_DRAFT_STORAGE_KEY);
    if (!raw) {
      return initialState;
    }

    const parsed = JSON.parse(raw) as { version?: number; state?: Partial<WizardState> } | Partial<WizardState>;
    const restored = 'state' in (parsed as object) ? (parsed as { state?: Partial<WizardState> }).state : parsed;
    if (!restored || typeof restored !== 'object') {
      return initialState;
    }

    return {
      ...initialState,
      ...restored,
      // Draft reopen should never restore transient map/modal-only states as active.
      isMinimizedForMap: false,
      demandHelpStatus: 'idle',
      generateTripsStatus: 'idle',
      fleetHelpStatus: 'idle',
      fleetHelpOpen: false,
    };
  } catch {
    return initialState;
  }
}

function toPersistableState(state: WizardState): WizardState {
  return {
    ...state,
    isMinimizedForMap: false,
    demandHelpStatus: state.demandHelpStatus === 'loading' ? 'idle' : state.demandHelpStatus,
    generateTripsStatus: state.generateTripsStatus === 'loading' ? 'idle' : state.generateTripsStatus,
    fleetHelpStatus: state.fleetHelpStatus === 'loading' ? 'idle' : state.fleetHelpStatus,
    fleetHelpOpen: false,
  };
}

export function useWizardState(
  polygonCount: number,
  hasWheelchairTripsForRideLayer?: (rideLayerId: string) => boolean
) {
  const [state, setState] = useState<WizardState>(() => getHydratedInitialState());

  const stepIds = useMemo(() => computeWizardStepIds(state), [state]);
  const currentStepId = stepIds[Math.min(Math.max(state.currentStep, 1), stepIds.length) - 1] ?? 'setup';

  const setStep = (nextStep: number) => {
    setState((prev) => {
      const max = computeWizardStepIds(prev).length;
      return {
        ...prev,
        currentStep: Math.min(Math.max(nextStep, 1), max),
      };
    });
  };

  const setStepById = (stepId: WizardStepId) => {
    setState((prev) => {
      const ids = computeWizardStepIds(prev);
      const idx = ids.indexOf(stepId);
      return {
        ...prev,
        currentStep: idx >= 0 ? idx + 1 : prev.currentStep,
      };
    });
  };

  const setServicePreset = (preset: ServicePreset) => {
    setState((prev) => {
      const nextPassengers =
        preset === 'paratransit'
          ? { ...prev.savedParatransitPassengerDistribution }
          : { ...DEFAULT_PASSENGERS_NON_PARATRANSIT };

      const next = {
        ...prev,
        servicePreset: preset,
        demandPattern: demandPatternByPreset[preset],
        operatingPeriodsUtc: getDefaultPeriodsForPreset(preset),
        serviceMode: preset === 'school' ? 'door_to_hub' : prev.serviceMode,
        passengerDistribution: nextPassengers,
        capacityStandard: preset === 'school' ? 50 : preset === 'paratransit' ? 12 : prev.capacityStandard,
        capacityWheelchair: preset === 'paratransit' ? 2 : prev.capacityWheelchair,
        simulationName: buildSimulationName(preset),
        fleetTimelineSlotStatuses: {},
        fleetDefaultsSeededFromDemand: false,
      };

      const max = computeWizardStepIds(next).length;
      next.currentStep = Math.min(next.currentStep, max);
      return next;
    });
  };

  const setMinimizedForMap = (isMinimizedForMap: boolean) => {
    setState((prev) => ({ ...prev, isMinimizedForMap }));
  };

  const setOperatingPeriod = (id: string, patch: Partial<Pick<OperatingPeriodUtc, 'startUtc' | 'endUtc'>>) => {
    setState((prev) => ({
      ...prev,
      operatingPeriodsUtc: prev.operatingPeriodsUtc.map((period) => (period.id === id ? { ...period, ...patch } : period)),
    }));
  };

  const setOperatingPeriods = (operatingPeriodsUtc: OperatingPeriodUtc[]) => {
    setState((prev) => ({
      ...prev,
      operatingPeriodsUtc,
    }));
  };

  const addOperatingPeriod = () => {
    setState((prev) => ({
      ...prev,
      operatingPeriodsUtc: [
        ...prev.operatingPeriodsUtc,
        makeOperatingPeriod(`custom_${prev.operatingPeriodsUtc.length + 1}`, '09:00', '17:00'),
      ],
    }));
  };

  const removeOperatingPeriod = (id: string) => {
    setState((prev) => {
      if (prev.operatingPeriodsUtc.length <= 1) return prev;
      return {
        ...prev,
        operatingPeriodsUtc: prev.operatingPeriodsUtc.filter((period) => period.id !== id),
      };
    });
  };

  const setServiceMode = (serviceMode: ServiceMode) => {
    setState((prev) => ({
      ...prev,
      serviceMode,
    }));
  };

  const setHubCount = (hubCount: number) => {
    setState((prev) => ({ ...prev, hubCount: Math.max(0, hubCount) }));
  };

  const beginHubPlacement = (pointCount: number) => {
    setState((prev) => ({
      ...prev,
      hubBaselinePointCount: pointCount,
    }));
  };

  const syncHubCountFromPoints = (pointCount: number) => {
    setState((prev) => {
      const baseline = prev.hubBaselinePointCount ?? pointCount;
      return {
        ...prev,
        hubCount: Math.max(prev.hubCount, pointCount - baseline),
      };
    });
  };

  const setDemandTrips = (demandTrips: number | null) => {
    setState((prev) => ({ ...prev, demandTrips, demandError: null }));
  };

  const setDemandHelpStatus = (demandHelpStatus: WizardState['demandHelpStatus']) => {
    setState((prev) => ({ ...prev, demandHelpStatus }));
  };

  const setGenerateTripsStatus = (generateTripsStatus: WizardState['generateTripsStatus']) => {
    setState((prev) => ({ ...prev, generateTripsStatus }));
  };

  const setDemandInsight = (demandInsight: DemandInsight | null) => {
    setState((prev) => ({ ...prev, demandInsight }));
  };

  const setDemandError = (demandError: string | null) => {
    setState((prev) => ({ ...prev, demandError }));
  };

  const setDemandHelperStatusText = (demandHelperStatusText: string | null) => {
    setState((prev) => ({ ...prev, demandHelperStatusText }));
  };

  const setAuthoritativeRideSource = (rideLayerId: string | null, source: WizardState['rideSource']) => {
    setState((prev) => ({
      ...prev,
      authoritativeRideLayerId: rideLayerId,
      rideSource: rideLayerId ? source : 'none',
      demandHelperStatusText:
        rideLayerId && source === 'wizard_helper'
          ? 'Wizard-linked helper trips are authoritative and will be used for simulation.'
          : prev.demandHelperStatusText,
    }));
  };

  const setPassengerDistribution = (patch: Partial<WizardState['passengerDistribution']>) => {
    setState((prev) => {
      const next = { ...prev.passengerDistribution, ...patch };
      const standardPct = Math.max(0, Math.min(100, Number(next.standardPct) || 0));
      const wheelchairPct = Math.max(0, Math.min(100, Number(next.wheelchairPct) || 0));
      const distribution = {
        standardPct,
        wheelchairPct,
      };
      return {
        ...prev,
        passengerDistribution: distribution,
        savedParatransitPassengerDistribution:
          prev.servicePreset === 'paratransit' ? distribution : prev.savedParatransitPassengerDistribution,
      };
    });
  };

  const syncHubWeights = (hubs: Array<{ id: string; name: string }>) => {
    setState((prev) => {
      if (hubs.length === 0) {
        if (prev.hubWeights.length === 0) return prev;
        return { ...prev, hubWeights: [] };
      }

      if (hubs.length === 1) {
        const nextSingle = [{ id: hubs[0].id, name: hubs[0].name, percentage: 100 }];
        if (hubWeightsEquivalent(prev.hubWeights, nextSingle)) {
          return prev;
        }
        return {
          ...prev,
          hubWeights: nextSingle,
        };
      }

      const existingMap = new Map(prev.hubWeights.map((hub) => [hub.id, hub]));
      const existingIds = new Set(prev.hubWeights.map((hub) => hub.id));
      const hasOverlap = hubs.some((hub) => existingIds.has(hub.id));
      const isHubSetChanged =
        hubs.length !== prev.hubWeights.length ||
        hubs.some((hub, index) => prev.hubWeights[index]?.id !== hub.id);

      if (!hasOverlap) {
        const defaults = buildEqualIntegerSplit(hubs.length);
        const nextHubWeights = hubs.map((hub, index) => ({
          id: hub.id,
          name: hub.name,
          percentage: defaults[index],
        }));
        if (hubWeightsEquivalent(prev.hubWeights, nextHubWeights)) {
          return prev;
        }
        return {
          ...prev,
          hubWeights: nextHubWeights,
        };
      }

      // If the user has not manually tuned hub weighting, keep defaults fair whenever hubs change.
      if (!prev.hubWeightsTouched && isHubSetChanged) {
        const defaults = buildEqualIntegerSplit(hubs.length);
        const nextHubWeights = hubs.map((hub, index) => ({
          id: hub.id,
          name: hub.name,
          percentage: defaults[index],
        }));
        if (hubWeightsEquivalent(prev.hubWeights, nextHubWeights)) {
          return prev;
        }
        return {
          ...prev,
          hubWeights: nextHubWeights,
        };
      }

      const merged: HubWeight[] = hubs.map((hub) => ({
        id: hub.id,
        name: hub.name,
        percentage: existingMap.has(hub.id) ? clampIntegerPercent(existingMap.get(hub.id)!.percentage) : 0,
      }));

      const newHubIndexes = merged
        .map((hub, index) => ({ hub, index }))
        .filter(({ hub }) => !existingMap.has(hub.id))
        .map(({ index }) => index);

      let existingTotal = merged.reduce((sum, hub, index) => {
        if (newHubIndexes.includes(index)) return sum;
        return sum + hub.percentage;
      }, 0);
      let remaining = 100 - existingTotal;

      // For manual mode with added hubs, make room so each new hub receives at least 1%.
      if (newHubIndexes.length > 0 && remaining < newHubIndexes.length) {
        let deficit = newHubIndexes.length - remaining;
        while (deficit > 0) {
          const donorIndex = merged.findIndex((hub, index) => !newHubIndexes.includes(index) && hub.percentage > 0);
          if (donorIndex < 0) break;
          merged[donorIndex].percentage -= 1;
          deficit -= 1;
          remaining += 1;
        }
      }

      if (newHubIndexes.length > 0 && remaining > 0) {
        const split = distributeUnitsEvenly(remaining, newHubIndexes.length);
        newHubIndexes.forEach((idx, splitIndex) => {
          merged[idx].percentage = split[splitIndex];
        });
      }

      const rebalanced = rebalanceToExact100(merged);
      if (hubWeightsEquivalent(prev.hubWeights, rebalanced)) {
        return prev;
      }
      return {
        ...prev,
        hubWeights: rebalanced,
      };
    });
  };

  const setHubWeight = (hubId: string, percentage: number) => {
    setState((prev) => {
      const clamped = clampIntegerPercent(percentage);
      const hubWeights = prev.hubWeights.map((hub) => (hub.id === hubId ? { ...hub, percentage: clamped } : hub));
      return { ...prev, hubWeights: rebalanceToExact100(hubWeights), hubWeightsTouched: true };
    });
  };

  const setVehicleCount = (vehicleCount: number) => {
    setState((prev) => {
      const nextVehicleCount = Math.max(0, Math.round(Number(vehicleCount) || 0));
      const switchedToManual = prev.fleetSource === 'wizard_helper' && nextVehicleCount !== prev.vehicleCount;
      return {
        ...prev,
        vehicleCount: nextVehicleCount,
        fleetSource: switchedToManual ? 'manual' : prev.fleetSource === 'wizard_helper' ? prev.fleetSource : 'manual',
        authoritativeFleetId: switchedToManual ? null : prev.authoritativeFleetId,
        fleetHelpStatus: switchedToManual ? 'idle' : prev.fleetHelpStatus,
        fleetDefaultsSeededFromDemand: true,
      };
    });
  };

  const setCapacityStandard = (capacityStandard: number) => {
    setState((prev) => {
      const nextCapacityStandard = Math.max(0, Math.round(Number(capacityStandard) || 0));
      const switchedToManual = prev.fleetSource === 'wizard_helper' && nextCapacityStandard !== prev.capacityStandard;
      return {
        ...prev,
        capacityStandard: nextCapacityStandard,
        fleetSource: switchedToManual ? 'manual' : prev.fleetSource === 'wizard_helper' ? prev.fleetSource : 'manual',
        authoritativeFleetId: switchedToManual ? null : prev.authoritativeFleetId,
        fleetHelpStatus: switchedToManual ? 'idle' : prev.fleetHelpStatus,
        fleetDefaultsSeededFromDemand: true,
      };
    });
  };

  const setCapacityWheelchair = (capacityWheelchair: number) => {
    setState((prev) => {
      const nextCapacityWheelchair = Math.max(0, Math.round(Number(capacityWheelchair) || 0));
      const switchedToManual = prev.fleetSource === 'wizard_helper' && nextCapacityWheelchair !== prev.capacityWheelchair;
      return {
        ...prev,
        capacityWheelchair: nextCapacityWheelchair,
        fleetSource: switchedToManual ? 'manual' : prev.fleetSource === 'wizard_helper' ? prev.fleetSource : 'manual',
        authoritativeFleetId: switchedToManual ? null : prev.authoritativeFleetId,
        fleetHelpStatus: switchedToManual ? 'idle' : prev.fleetHelpStatus,
        fleetDefaultsSeededFromDemand: true,
      };
    });
  };

  const setFleetTimelineSlotStatus = (slotKey: string, status: WizardTimelineSlotStatus) => {
    setState((prev) => ({
      ...prev,
      fleetTimelineSlotStatuses: {
        ...prev.fleetTimelineSlotStatuses,
        [slotKey]: status,
      },
    }));
  };

  const clearFleetTimelineSlotStatuses = () => {
    setState((prev) => ({
      ...prev,
      fleetTimelineSlotStatuses: {},
    }));
  };

  const setFleetDefaultsSeededFromDemand = (seeded: boolean) => {
    setState((prev) => ({
      ...prev,
      fleetDefaultsSeededFromDemand: seeded,
    }));
  };

  const setFleetHelpStatus = (fleetHelpStatus: WizardState['fleetHelpStatus']) => {
    setState((prev) => ({ ...prev, fleetHelpStatus }));
  };

  const setFleetHelpOpen = (fleetHelpOpen: boolean) => {
    setState((prev) => ({ ...prev, fleetHelpOpen }));
  };

  const setAuthoritativeFleetSource = (fleetId: string | null, source: WizardState['fleetSource']) => {
    setState((prev) => ({
      ...prev,
      authoritativeFleetId: fleetId,
      fleetSource: fleetId ? source : 'none',
    }));
  };

  const setFeelLevel = (feelLevel: 1 | 2 | 3 | 4 | 5) => {
    setState((prev) => ({
      ...prev,
      feelLevel,
      feelSlope: FEEL_PROFILES[feelLevel].slope,
      feelInterceptMinutes: FEEL_PROFILES[feelLevel].interceptMinutes,
    }));
  };

  const setMaxTripTimeMinutes = (maxTripTimeMinutes: number) => {
    setState((prev) => ({
      ...prev,
      maxTripTimeMinutes: Math.max(1, Math.round(Number(maxTripTimeMinutes) || 1)),
    }));
  };

  const setSimulationName = (simulationName: string) => {
    setState((prev) => ({ ...prev, simulationName }));
  };

  const hasValidPeriods = useCallback(
    (periods: OperatingPeriodUtc[]) =>
      periods.length > 0 &&
      periods.every((period) => {
        const isValidUtcTime = (value: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
        if (!isValidUtcTime(period.startUtc) || !isValidUtcTime(period.endUtc)) return false;
        return period.startUtc < period.endUtc;
      }),
    []
  );

  const requiresHubs =
    state.servicePreset !== 'paratransit' && (state.serviceMode === 'door_to_hub' || state.serviceMode === 'hub_to_hub');

  const stepValidation: WizardStepValidation = useMemo(() => {
    if (currentStepId === 'setup') {
      if (!state.servicePreset) {
        return { canProceed: false, reason: 'validation.selectServicePreset' };
      }
      return { canProceed: true };
    }

    if (currentStepId === 'configuration') {
      if (!state.serviceMode) {
        return { canProceed: false, reason: 'validation.chooseServiceMode' };
      }
      if (polygonCount <= 0) {
        return { canProceed: false, reason: 'validation.atLeastOneZone' };
      }
      if (requiresHubs && state.hubCount < 1) {
        return { canProceed: false, reason: 'validation.atLeastOneHub' };
      }
      return { canProceed: true };
    }

    if (currentStepId === 'demand') {
      if (state.demandHelpStatus === 'loading' || state.generateTripsStatus === 'loading') {
        return {
          canProceed: false,
          reason: 'validation.waitForDemand',
        };
      }
      const demandTrips = state.demandTrips;
      if (typeof demandTrips !== 'number' || demandTrips <= 0) {
        return {
          canProceed: false,
          reason: 'validation.enterTrips',
        };
      }
      if (demandTrips > MAX_WIZARD_GENERATED_TRIPS) {
        return {
          canProceed: false,
          reason: 'validation.tripLimit',
        };
      }
      if (!state.authoritativeRideLayerId) {
        return {
          canProceed: false,
          reason: 'validation.generateTrips',
        };
      }
      return {
        canProceed: true,
      };
    }

    if (currentStepId === 'passenger_types') {
      const passengerTotal =
        Math.round(state.passengerDistribution.standardPct) + Math.round(state.passengerDistribution.wheelchairPct);
      const validPassengerSplit = passengerTotal === 100;
      return {
        canProceed: validPassengerSplit,
        reason: validPassengerSplit ? undefined : 'validation.passengerSplit',
      };
    }

    if (currentStepId === 'hub_weighting') {
      const hubTotal = state.hubWeights.reduce((sum, hub) => sum + Math.round(hub.percentage), 0);
      const hasHubWeights = state.hubWeights.length > 0;
      const validHubSplit = hasHubWeights && hubTotal === 100;
      return {
        canProceed: validHubSplit,
        reason: validHubSplit ? undefined : 'validation.hubSplit',
      };
    }

    if (currentStepId === 'fleet') {
      if (!state.authoritativeRideLayerId) {
        return {
          canProceed: false,
          reason: 'validation.generateTrips',
        };
      }
      if (state.fleetSource === 'wizard_helper' && state.authoritativeFleetId) {
        return { canProceed: true };
      }
      const totalCapacity = state.capacityStandard + state.capacityWheelchair;
      const valid =
        state.vehicleCount > 0 &&
        state.capacityStandard >= 0 &&
        state.capacityWheelchair >= 0 &&
        totalCapacity > 0;
      if (
        valid &&
        state.capacityWheelchair <= 0 &&
        state.authoritativeRideLayerId &&
        hasWheelchairTripsForRideLayer?.(state.authoritativeRideLayerId)
      ) {
        return {
          canProceed: false,
          reason: 'validation.wheelchairRequired',
        };
      }
      return {
        canProceed: valid,
        reason: valid ? undefined : 'validation.vehicleSeats',
      };
    }

    if (currentStepId === 'service_configuration') {
      const valid = state.maxTripTimeMinutes > 0;
      return {
        canProceed: valid,
        reason: valid ? undefined : 'validation.maxTripTime',
      };
    }

    if (currentStepId === 'summary') {
      if (!hasValidPeriods(state.operatingPeriodsUtc)) {
        return {
          canProceed: false,
          reason: 'validation.operatingPeriods',
        };
      }
      const valid = state.simulationName.trim().length > 0;
      return {
        canProceed: valid,
        reason: valid ? undefined : 'validation.simulationName',
      };
    }

    return { canProceed: true };
  }, [
    currentStepId,
    hasValidPeriods,
    polygonCount,
    requiresHubs,
    state.authoritativeFleetId,
    state.authoritativeRideLayerId,
    state.capacityStandard,
    state.capacityWheelchair,
    state.demandHelpStatus,
    state.demandTrips,
    state.fleetSource,
    state.hubCount,
    state.hubWeights,
    state.maxTripTimeMinutes,
    state.operatingPeriodsUtc,
    state.passengerDistribution.standardPct,
    state.passengerDistribution.wheelchairPct,
    state.generateTripsStatus,
    state.serviceMode,
    state.servicePreset,
    state.simulationName,
    state.vehicleCount,
    hasWheelchairTripsForRideLayer,
  ]);

  const steps: WizardStepDescriptor[] = useMemo(
    () =>
      stepIds.map((id, index) => ({
        id,
        label: STEP_LABELS[id],
        enabled: index + 1 <= state.currentStep,
        completed: index + 1 < state.currentStep,
        skipped: false,
      })),
    [state.currentStep, stepIds]
  );

  const reset = () => {
    setState(initialState);
  };

  const saveDraft = () => {
    try {
      const payload = {
        version: WIZARD_DRAFT_VERSION,
        state: toPersistableState(state),
      };
      localStorage.setItem(WIZARD_DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Best-effort persistence only.
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
    } catch {
      // No-op.
    }
  };

  return {
    state,
    steps,
    currentStepId,
    totalSteps: stepIds.length,
    setStep,
    setStepById,
    setServicePreset,
    setMinimizedForMap,
    setOperatingPeriod,
    setOperatingPeriods,
    addOperatingPeriod,
    removeOperatingPeriod,
    setServiceMode,
    setHubCount,
    beginHubPlacement,
    syncHubCountFromPoints,
    setDemandTrips,
    setDemandHelpStatus,
    setGenerateTripsStatus,
    setDemandInsight,
    setDemandError,
    setDemandHelperStatusText,
    setAuthoritativeRideSource,
    setPassengerDistribution,
    syncHubWeights,
    setHubWeight,
    setVehicleCount,
    setCapacityStandard,
    setCapacityWheelchair,
    setFleetHelpStatus,
    setFleetHelpOpen,
    setAuthoritativeFleetSource,
    setFleetTimelineSlotStatus,
    clearFleetTimelineSlotStatuses,
    setFleetDefaultsSeededFromDemand,
    setFeelLevel,
    setMaxTripTimeMinutes,
    setSimulationName,
    stepValidation,
    reset,
    saveDraft,
    clearDraft,
  };
}
